import * as React from 'react';
import {connect} from 'react-redux';

import type {ListQuery} from 'nutshell-core/entities/fetch-list';
import * as Leads from 'nutshell-core/leads';
import {Outcomes} from 'nutshell-core/outcomes';
import type {
    ListItem,
    FilterObject,
    LeadId,
    OutcomeId,
    Outcome,
    CompetitorMapId,
    ProductMapPatch,
} from 'nutshell-core/types';
import {VisualPipeline} from 'nutshell-core/visual-pipeline';
import type {LeadClosingConfirmation} from 'nutshell-core/visual-pipeline';

import {addNotification} from '../../notifications/index';
import type {NutshellState} from '../../store';
import {formatCurrencyValue} from '../../util/format-currency-value';

import {ConfirmationFormLoseLead, ConfirmationFormWinLead} from '../lead/shared/components';

import {VisualPipelineCloseConfirmationPopover} from './visual-pipeline-close-confirmation-popover';
import {VisualPipelineCloseLeadDropTarget} from './visual-pipeline-close-lead-drop-target';

import styles from './visual-pipeline-close-lead-bar.module.css';

type StateProps = {
    leadClosingConfirmation: LeadClosingConfirmation;
    listItemsById: Partial<Record<LeadId, ListItem>>;
    outcomes: Partial<Record<OutcomeId, Outcome>>;
};

type OwnProps = {
    isOpen: boolean;
    filters: FilterObject[];
    selectedSort: string;
    fieldsToDisplay: string[];
};

type DispatchProps = {
    onCancelCloseLead: () => any;
    onConfirmCloseLead: (
        leadId: string,
        outcomeId: string,
        query: ListQuery,
        patchLeadData?: any
    ) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

type PatchDataCloseLead =
    | {
          productMaps: ProductMapPatch[];
      }
    | {
          competitorMaps: CompetitorMapId[];
      };

const mapStateToProps = function (state: NutshellState): StateProps {
    return {
        leadClosingConfirmation: VisualPipeline.getLeadClosingConfirmation(state),
        outcomes: Outcomes.getAllOutcomes(state),
        // @ts-expect-error
        listItemsById: Leads.getLeadListItemMap(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onCancelCloseLead: () => {
            dispatch(
                VisualPipeline.updateLeadClosingConfirmation({
                    leadId: null,
                    closeType: null,
                })
            );
        },
        onConfirmCloseLead: (leadId, outcomeId, query, patchLeadData) => {
            return dispatch(
                VisualPipeline.onConfirmCloseLead(leadId, outcomeId, query, patchLeadData)
            );
        },
    };
};

/*
Conditionally shown component that lives at the bottom of the Visual Pipeline
and is responsible for closing a lead from the board. Houses the drop targets
and close "confirmation forms" (yes I do want to win this lead).
 */
export class VisualPipelineCloseLeadBarComponent extends React.PureComponent<
    Props,
    {
        errorMessage?: string;
    }
> {
    cancelLeadRef: HTMLElement | null | undefined;
    loseLeadRef: HTMLElement | null | undefined;
    winLeadRef: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            errorMessage: undefined,
        };
    }

    render() {
        const closeType = this.props.leadClosingConfirmation.closeType;
        const leadIdClosing = this.props.leadClosingConfirmation.id;
        const isContainerOpen = this.props.isOpen || this.props.leadClosingConfirmation.id;

        return (
            <div className={isContainerOpen ? styles['container-open'] : styles.container}>
                <VisualPipelineCloseLeadDropTarget
                    type='cancel'
                    // @ts-expect-error
                    buttonRef={(node) => {
                        this.cancelLeadRef = node;
                    }}
                />
                <div className={styles['button-section-right']}>
                    <VisualPipelineCloseLeadDropTarget
                        type='lose'
                        // @ts-expect-error
                        buttonRef={(node) => {
                            this.loseLeadRef = node;
                        }}
                    />
                    <VisualPipelineCloseLeadDropTarget
                        type='win'
                        // @ts-expect-error
                        buttonRef={(node) => {
                            this.winLeadRef = node;
                        }}
                    />
                </div>
                {closeType === 'cancel' && leadIdClosing ? (
                    <VisualPipelineCloseConfirmationPopover
                        anchor={this.cancelLeadRef}
                        location='top'
                        headerText='Cancel lead'
                        onCancel={this.props.onCancelCloseLead}
                        // @ts-expect-error
                        listItem={this.props.listItemsById[leadIdClosing]}
                        fieldsToDisplay={this.props.fieldsToDisplay}
                    >
                        <ConfirmationFormLoseLead
                            leadId={leadIdClosing}
                            isCancelling={true}
                            errorMessage={this.state.errorMessage}
                            onClearError={this.handleClearError}
                            onCancel={this.props.onCancelCloseLead}
                            onSubmit={this.handleSubmit}
                        />
                    </VisualPipelineCloseConfirmationPopover>
                ) : undefined}
                {closeType === 'lose' && leadIdClosing ? (
                    <VisualPipelineCloseConfirmationPopover
                        anchor={this.loseLeadRef}
                        location='top'
                        headerText='Lose lead'
                        onCancel={this.props.onCancelCloseLead}
                        // @ts-expect-error
                        listItem={this.props.listItemsById[leadIdClosing]}
                        fieldsToDisplay={this.props.fieldsToDisplay}
                    >
                        <ConfirmationFormLoseLead
                            leadId={leadIdClosing}
                            errorMessage={this.state.errorMessage}
                            onClearError={this.handleClearError}
                            onCancel={this.props.onCancelCloseLead}
                            onSubmit={this.handleSubmit}
                        />
                    </VisualPipelineCloseConfirmationPopover>
                ) : undefined}
                {closeType === 'win' && leadIdClosing ? (
                    <VisualPipelineCloseConfirmationPopover
                        anchor={this.winLeadRef}
                        location='top'
                        headerText='Congrats!'
                        onCancel={this.props.onCancelCloseLead}
                        // @ts-expect-error
                        listItem={this.props.listItemsById[leadIdClosing]}
                        fieldsToDisplay={this.props.fieldsToDisplay}
                    >
                        <ConfirmationFormWinLead
                            leadId={leadIdClosing}
                            errorMessage={this.state.errorMessage}
                            onClearError={this.handleClearError}
                            onCancel={this.props.onCancelCloseLead}
                            onSubmit={this.handleSubmit}
                        />
                    </VisualPipelineCloseConfirmationPopover>
                ) : undefined}
            </div>
        );
    }

    handleClearError = () => {
        this.setState({errorMessage: undefined});
    };

    /**
     * Called when the "confirm" button is pressed on the our confirmation
     * form. Updates our local state accordingly while making a request to win
     * the lead.
     *
     * If the request is successful, we'll fire a callback so that our store's
     * state of the world knows we've successfully closed the lead, and can dispatch
     * the proper updates.
     *
     * We have two possible errors here:
     * 1) The request can fail with a network error
     * 2) The lead actually can't be won, as there are incomplete required steps
     *
     * @param  {string} leadId         - API-id of the lead to be closed
     * @param  {string} outcomeId      - API-id of the outcome of the lead
     * @param  {Object} patchLeadData  - Additional data to use in the request
     * @return {void}
     */
    handleSubmit = (leadId: LeadId, outcomeId: OutcomeId, patchLeadData?: PatchDataCloseLead) => {
        const leadListQuery = {
            filter: this.props.filters,
            sort: this.props.selectedSort,
        } as const;

        this.props.onConfirmCloseLead(leadId, outcomeId, leadListQuery, patchLeadData).then(
            () => {
                // If we got a successful response here, the lead was
                // closed and our state will have closed this component
                const notification = this.getNotificationForClosedLead(
                    leadId,
                    outcomeId,
                    patchLeadData
                );

                if (notification) {
                    addNotification(notification.title, notification.options);
                }
            },
            // @ts-expect-error
            (err) => {
                this.setState({
                    errorMessage:
                        err && err.message
                            ? err.message
                            : 'Oh no! Something went wrong closing lead.',
                });
            }
        );
    };

    /**
     * Given a leadId and outcomeId, generates a decently user-friendly growl
     * notification for closing a lead.
     *
     * @param  {string} leadId        - Lead API-id
     * @param  {string} outcomeId     - Outcome API-id
     * @param  {string} data          - Patch data for lead
     * @return {Object}               - Data necessary to generate a notification
     */
    getNotificationForClosedLead(
        leadId: LeadId,
        outcomeId: OutcomeId,
        data?: PatchDataCloseLead
    ):
        | {
              title: string;
              options: any;
          }
        | null
        | undefined {
        const listItem = this.props.listItemsById[leadId];
        const outcome = this.props.outcomes[outcomeId];

        if (!listItem || !outcome) {
            return null;
        }

        const onClickHandler = (e: Event) => {
            // @ts-expect-error
            if (e.metaKey || e.ctrlKey) {
                window.open(listItem.htmlUrl);
            } else {
                // @ts-expect-error
                window.location = listItem.htmlUrl;
            }
        };

        // If the lead is won, we'll have productMaps and can generate a
        // dynamic value from what the user entered when winning a lead
        const productMaps =
            // @ts-expect-error
            data && data.productMaps && data.productMaps.length ? data.productMaps : null;
        const leadValue = productMaps
            ? formatCurrencyValue(
                  Leads.getLeadValueFromProductMaps(productMaps),
                  productMaps[0].price.currency
              )
            : listItem.fields.value.value;

        return {
            title: outcome.status === 'won' ? 'Congratulations!' : `Lead ${outcome.status}`,
            options: {
                body: `${listItem.primaryName} worth ${leadValue} was ${outcome.status}`,
                icon: 'check',
                onClick: onClickHandler,
            },
        };
    }
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const VisualPipelineCloseLeadBar = connector(VisualPipelineCloseLeadBarComponent);
